import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Gem, Loader2, AlertCircle, RotateCcw, TrendingUp, Package } from 'lucide-react'
import confetti from 'canvas-confetti'
import useCasesStore from '../store/useCasesStore'
import useAppStore from '../store/useAppStore'
import useInventoryStore from '../store/useInventoryStore'
import { inventoryApi } from '../services'
import CaseRoulette from '../components/CaseRoulette'
import Loader from '../components/Loader'
import { toast } from 'react-hot-toast'
import useAudioManager from '../hooks/useAudioManager'
import useUpdateBlocker from '../hooks/useUpdateBlocker'

const CaseOpening = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const fromOnboarding = useRef(location.state?.fromOnboarding === true)
  const { t } = useTranslation()
  const [showResult, setShowResult] = useState(false)
  const [startRoulette, setStartRoulette] = useState(false)
  const [isWaitingForOpening, setIsWaitingForOpening] = useState(true)
  const [isSelling, setIsSelling] = useState(false)
  const [hasSpinCompleted, setHasSpinCompleted] = useState(false)
  const [isNavigating, setIsNavigating] = useState(false)

  // Ref для интервала конфетти
  const confettiIntervalRef = useRef(null)
  const hasPlayedEffects = useRef(false) // Флаг для предотвращения повторного воспроизведения эффектов
  const hasRequestedOpeningRef = useRef(false)

  // Инициализация Audio Manager через глобальный audioService
  const { playSound, stopAllSounds } = useAudioManager({
    win: 'winV3',
    sell: 'sell',
    goodWin: 'winV1'
  })
  
  const { 
    currentCase,
    loadCaseDetails,
    openCase, 
    isOpening, 
    openingResult, 
    error,
    clearOpeningResult,
    clearError,
    loadCases
  } = useCasesStore()
  const { loadProfile } = useAppStore()
  useUpdateBlocker(isOpening || isSelling || isNavigating || (!error && !hasSpinCompleted))
  // useInventoryStore используется через getState() в useEffect

  const caseId = id // ID кейса - строка, не число
  // Используем currentCase (загруженный через API)
  const caseData = currentCase
  const openAgainLabel = caseData?.name
    ? `${t('caseOpening.openAgain')} ${caseData.name}`
    : t('caseOpening.openAgain')
  
  // ID велком-кейса для особой логики отображения кнопок
  const WELCOME_CASE_ID = '00000000000000000000000000000000'
  const isWelcomeCase = caseId === WELCOME_CASE_ID

  // Функция для сокращения состояния износа
  const shortenWear = (text) => {
    return text
      .replace(/\(Factory New\)/gi, 'FN')
      .replace(/\(Minimal Wear\)/gi, 'MW')
      .replace(/\(Field-Tested\)/gi, 'FT')
      .replace(/\(Well-Worn\)/gi, 'WW')
      .replace(/\(Battle-Scarred\)/gi, 'BS')
  }

  // Остановка всех звуков при уходе со страницы
  useEffect(() => {
    return () => {
      stopAllSounds()
      console.log('🔇 CaseOpening: все звуки остановлены при размонтировании')
    }
  }, [])

  // Проверка легитимности открытия - защита от случайного повторного открытия
  useEffect(() => {
    // Если пришли не с кнопки открытия (нет флага allowOpening) - редирект на список кейсов
    if (!location.state?.allowOpening) {
      console.log('⚠️ Попытка открыть кейс без флага allowOpening - редирект на /cases')
      navigate('/cases', { replace: true })
      return
    }
    
    // Очищаем флаг из истории, чтобы при обновлении страницы он пропал
    window.history.replaceState({}, document.title)
  }, [location, navigate])

  // Очистка интервала конфетти при размонтировании
  useEffect(() => {
    return () => {
      if (confettiIntervalRef.current) {
        clearInterval(confettiIntervalRef.current)
        confettiIntervalRef.current = null
      }
    }
  }, [])

  // Функция запуска конфетти для больших выигрышей
  const triggerConfetti = () => {
    // Очищаем предыдущий интервал, если он существует
    if (confettiIntervalRef.current) {
      clearInterval(confettiIntervalRef.current)
      confettiIntervalRef.current = null
    }

    const duration = 3000 // 3 секунды
    const animationEnd = Date.now() + duration
    const defaults = { 
      startVelocity: 30, 
      spread: 360, 
      ticks: 60, 
      zIndex: 9999,
      colors: ['#00FF7F', '#4EFFC6', '#FFD700', '#FF6B6B', '#4ECDC4']
    }

    const randomInRange = (min, max) => Math.random() * (max - min) + min

    confettiIntervalRef.current = setInterval(() => {
      const timeLeft = animationEnd - Date.now()

      if (timeLeft <= 0) {
        clearInterval(confettiIntervalRef.current)
        confettiIntervalRef.current = null
        return
      }

      const particleCount = 50 * (timeLeft / duration)

      // Запускаем конфетти с двух сторон
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
      })
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
      })
    }, 250)
  }

  useEffect(() => {
    // Clear previous results
    clearOpeningResult()
    clearError()
    setShowResult(false)
    setStartRoulette(false)
    setHasSpinCompleted(false)
    setIsWaitingForOpening(true)
    hasPlayedEffects.current = false
    hasRequestedOpeningRef.current = false

    // Всегда загружаем детали кейса при прямом переходе
    if (caseId && (!currentCase || (currentCase?.id && String(currentCase.id) !== String(caseId)))) {
      loadCaseDetails(caseId)
      return
    }
  }, [caseId, clearError, clearOpeningResult, currentCase, loadCaseDetails])

  useEffect(() => {
    if (!location.state?.allowOpening) return
    if (!caseData) return
    if (caseData?.id && String(caseData.id) !== String(caseId)) return
    if (hasRequestedOpeningRef.current) return

    hasRequestedOpeningRef.current = true
    setIsWaitingForOpening(true)

    openCase(caseId).catch((e) => {
      console.error('Failed to open case:', e)
    })
  }, [caseData, caseId, location.state, openCase])

  useEffect(() => {
    if (openingResult?.win && isWaitingForOpening) {
      setIsWaitingForOpening(false)
      setStartRoulette(true)
    }
  }, [openingResult, isWaitingForOpening])

  // Show result when opening is complete AND spin animation finished
  useEffect(() => {
    // Воспроизводим звук только если:
    // 1. Есть результат открытия (openingResult установлен)
    // 2. Анимация рулетки завершена (hasSpinCompleted = true)
    // 3. Эффекты еще не воспроизводились (защита от повторного срабатывания)
    if (openingResult && hasSpinCompleted && !hasPlayedEffects.current) {
      hasPlayedEffects.current = true // Помечаем что эффекты воспроизведены
      
      setShowResult(true)
      
      // Проверяем процент выигрыша
      const profitString = openingResult?.win?.profit || openingResult?.profit || '0%'
      const profitPercent = parseInt(profitString.replace(/[^0-9-]/g, ''))
      
      // Если профит > 6% или велком-кейс - воспроизводим звук удачного выигрыша и конфетти
      if (profitPercent > 6 || isWelcomeCase) {
        playSound('goodWin')
        // Небольшая задержка перед конфетти для синхронизации с анимацией
        setTimeout(() => {
          triggerConfetti()
        }, 10)
      } else {
        // Иначе обычный звук выигрыша
        playSound('win')
      }
      
      // Update user inventory (balance is handled by backend)
      // Перезагружаем инвентарь вместо локального добавления
      const { loadInventory } = useInventoryStore.getState()
      loadInventory()
    }
  }, [openingResult, hasSpinCompleted])

  const handleOpenCase = async () => {
    try {
      setShowResult(false)
      setStartRoulette(false)
      setHasSpinCompleted(false)
      setIsWaitingForOpening(true)
      hasPlayedEffects.current = false
      hasRequestedOpeningRef.current = true
      await openCase(caseId)
    } catch (error) {
      console.error('Failed to open case:', error)
    }
  }

  // Обработчик пропуска анимации рулетки
  const handleSkipAnimation = () => {
    if (hasSpinCompleted) return // Защита от повторных нажатий
    
    setHasSpinCompleted(true) // Помечаем что анимация завершена
    // showResult установится автоматически через useEffect когда придет openingResult и hasSpinCompleted = true
  }

  const handleSellItem = async () => {
    console.log('🔍 openingResult structure:', openingResult)
    
    // Ищем ID в разных возможных полях
    const itemId = openingResult?.inventory_id || openingResult?.id || openingResult?.win?.id
    
    if (!itemId) {
      console.error('❌ No inventory ID found in openingResult:', openingResult)
      toast.error(t('caseOpening.sellError'))
      return
    }
    
    console.log('✅ Using inventory ID for sale:', itemId)

    try {
      setIsSelling(true)
      // Используем правильный API endpoint для продажи
      await inventoryApi.saleItem(itemId)
      
      // Воспроизводим звук продажи
      playSound('sell')
      
      // Обновляем профиль для получения актуального баланса
      await loadProfile()
      
      toast.success(t('caseOpening.soldFor', { amount: openingResult.win?.cost }), {
        duration: 2000
      })
      
      // Небольшая задержка перед навигацией, чтобы пользователь увидел toast
      setTimeout(() => {
        clearOpeningResult()
        navigate(`/cases/${id}`)
      }, 500)
    } catch (error) {
      console.error('Sell error:', error)
      toast.error(t('caseOpening.sellError'))
    } finally {
      setIsSelling(false)
    }
  }

  const handleOpenAgain = () => {
    // Показываем лоадер перед навигацией
    setIsNavigating(true)
    // Перезапрашиваем список кейсов (приватка могла исчезнуть, лимиты измениться)
    loadCases()
    clearOpeningResult()
    setShowResult(false)
    setHasSpinCompleted(false)
    hasPlayedEffects.current = false // Сбрасываем флаг эффектов
    
    // Небольшая задержка для плавности
    setTimeout(() => {
      navigate(`/cases/${id}`)
    }, 100)
  }

  const handleBackToInventory = () => {
    clearOpeningResult()
    setHasSpinCompleted(false)
    hasPlayedEffects.current = false // Сбрасываем флаг эффектов
    // Принудительно перезагружаем инвентарь перед переходом
    const { loadInventory } = useInventoryStore.getState()
    loadInventory()
    navigate('/inventory')
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-[60vh] bg-dark-primary text-white flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <AlertCircle size={64} className="mx-auto text-red-500 mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">
            {t('caseOpening.error')}
          </h2>
          <p className="text-text-secondary mb-6">{error}</p>
          <div className="space-y-3">
            <button 
              onClick={handleOpenCase}
              className="w-full bg-accent-primary text-dark-primary font-bold py-3 rounded-lg hover:opacity-90 transition-all"
            >
              {t('caseOpening.tryAgain')}
            </button>
            <button 
              onClick={() => navigate('/cases')}
              className="w-full bg-transparent border border-text-secondary text-text-secondary font-semibold py-3 rounded-lg hover:bg-text-secondary/10 transition-all"
            >
              {t('caseOpening.backToCases')}
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Show loading state
  if (!caseData) {
    return <Loader message={t('caseOpening.loadingCase')} />
  }

  if (isWaitingForOpening) {
    return <Loader message={t('common.loading')} />
  }

  // Показываем полноэкранный лоадер при навигации
  if (isNavigating) {
    return (
      <div className="min-h-[60vh] bg-dark-primary text-white flex items-center justify-center">
        <Loader2 className="animate-spin text-accent-primary" size={48} />
      </div>
    )
  }

  return (
    <div 
      className="min-h-screen text-white overflow-hidden bg-center bg-no-repeat lg:relative lg:left-1/2 lg:w-screen lg:max-w-none lg:-translate-x-1/2 lg:bg-cover"
      style={{
        backgroundImage: 'url(/images/roulette-background.png)',
        backgroundAttachment: 'fixed'
      }}
    >
      {!showResult && !hasSpinCompleted && (
        <div className="flex items-center justify-center min-h-screen p-6">
          <CaseRoulette
            caseId={parseInt(id)}
            startAnimation={startRoulette}
            onAnimationComplete={() => setHasSpinCompleted(true)}
            onSkip={handleSkipAnimation}
            canSkip={startRoulette && !hasSpinCompleted && !isWelcomeCase}
          />
        </div>
      )}

      {/* Waiting for result after skip */}
      {hasSpinCompleted && !showResult && (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <Loader2 className="animate-spin text-accent-primary mx-auto mb-4" size={48} />
            <p className="text-text-secondary text-lg">{t('common.loading')}</p>
          </div>
        </div>
      )}

      {/* Result Screen */}
      {showResult && openingResult && (
        <div className="flex flex-col items-center justify-center min-h-screen p-6">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-accent-primary mb-2">
              {isWelcomeCase ? t('caseOpening.congratulationsWelcome') : t('caseOpening.congratulations')}
            </h1>
            {/* <p className="text-text-secondary text-lg">
              {t('caseOpening.youWon')}
            </p> */}
          </div>

          {/* Won Item with Effects */}
          <div className="relative mb-8 max-w-sm w-full animate-fadeUp">
            {/* Glow Effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-accent-primary/20 via-accent-secondary/30 to-accent-primary/20 rounded-lg blur-xl animate-pulse"></div>
            
            {/* Main Card */}
            <div className="relative bg-surface rounded-lg p-6 border-2 border-accent-primary/50 shadow-[0_0_30px_rgba(0,255,127,0.3)] animate-bounce-slow">
              <div className="text-center">
                {/* Item Image with Glow */}
                <div className="relative w-32 h-32 mx-auto mb-4">
                  <div className="absolute inset-0 bg-accent-primary/20 rounded-lg blur-md animate-pulse"></div>
                  <div className="relative w-full h-full bg-dark-primary rounded-lg border border-accent-primary/50 overflow-hidden">
                    <img 
                      src={openingResult.win?.img} 
                      alt={openingResult.win?.name}
                      className="w-full h-full object-contain p-2 animate-pulse"
                    />
                  </div>
                </div>
                
                {/* Item Name with Glow */}
                <h3 className="text-xl font-bold text-center mb-2 drop-shadow-[0_0_10px_rgba(0,255,127,0.5)]">
                  {openingResult.win?.name?.includes('|') ? (
                    <>
                      <div className="text-white">
                        {openingResult.win.name.split('|')[0].trim()}
                      </div>
                      <div className="text-text-secondary text-base mt-1 font-normal">
                        {shortenWear(openingResult.win.name.split('|')[1].trim())}
                      </div>
                    </>
                  ) : (
                    <div className="text-white">
                      {shortenWear(openingResult.win?.name)}
                    </div>
                  )}
                </h3>
                
                {/* Price with Animation */}
                <div className="flex items-center justify-center gap-3 text-accent-primary font-bold text-lg animate-pulse">
                  <Gem size={20} className="animate-spin-slow" />
                  <span className="drop-shadow-[0_0_10px_rgba(0,255,127,0.8)]">
                    {openingResult.win?.cost}
                  </span>
                  
                  {/* Profit Badge - скрываем для велком-кейса */}
                  {!isWelcomeCase && openingResult.win?.profit && (
                    <span 
                      className={`px-2 py-1 rounded-md text-sm font-medium ${
                        openingResult.win.profit.startsWith('+') 
                          ? 'bg-accent-primary text-dark-primary' 
                          : 'bg-gray-600 text-gray-300'
                      }`}
                    >
                      {openingResult.win.profit}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Sparkle Effects */}
            <div className="absolute -top-4 -left-4 w-4 h-4 bg-accent-primary rounded-full animate-ping"></div>
            <div className="absolute -top-2 -right-6 w-2 h-2 bg-accent-secondary rounded-full animate-ping delay-300"></div>
            <div className="absolute -bottom-4 -right-4 w-3 h-3 bg-accent-primary rounded-full animate-ping delay-700"></div>
            <div className="absolute -bottom-2 -left-6 w-2 h-2 bg-accent-secondary rounded-full animate-ping delay-500"></div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-4 w-full max-w-sm mx-auto">
            {isWelcomeCase ? (
              /* Кнопки для велком-кейса - только Апгрейд и Инвентарь */
              <>
                <button 
                  onClick={() => navigate('/upgrader', { state: { fromOnboarding: fromOnboarding.current } })}
                  className="w-full h-14 bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary font-bold rounded-lg hover:opacity-90 transition-all flex items-center justify-center gap-2 shadow-lg shadow-accent-primary/30"
                >
                  <TrendingUp size={20} />
                  {t('caseOpening.upgradeDrop')}
                </button>
                
                {/* <div className="flex items-center justify-center pt-2 text-sm">
                  <button 
                    onClick={handleBackToInventory}
                    className="flex items-center gap-1.5 text-text-secondary hover:text-white transition-colors"
                  >
                    <Package size={16} />
                    <span className="whitespace-nowrap">{t('caseOpening.backToInventory')}</span>
                  </button>
                </div> */}
              </>
            ) : (
              /* Кнопки для обычных кейсов - полный набор */
              <>
                {/* Primary Actions - Главные действия */}
                <div className="space-y-3">
                  <button 
                    onClick={handleSellItem}
                    disabled={isSelling}
                    className="w-full h-14 bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary font-bold rounded-lg hover:opacity-90 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-accent-primary/30"
                  >
                    {isSelling ? (
                      <>
                        <Loader2 size={20} className="animate-spin" />
                        <span>{t('inventory.selling')}</span>
                      </>
                    ) : (
                      <>
                        <span>{t('caseOpening.sellFor')}</span>
                        <Gem size={20} />
                        <span>{openingResult.win?.cost}</span>
                      </>
                    )}
                  </button>
                  
                  <button 
                    onClick={() => navigate('/upgrader')}
                    className="w-full h-14 bg-surface border border-accent-primary/30 text-white font-semibold rounded-lg hover:border-accent-primary/50 hover:bg-surface/80 transition-all flex items-center justify-center gap-2"
                  >
                    <TrendingUp size={20} className="text-accent-primary" />
                    {t('caseOpening.upgradeDrop')}
                  </button>
                </div>
                
                {/* Secondary Actions - Вторичные действия */}
                <div className="flex items-center justify-center gap-4 pt-2 text-sm">
                  {/* Кнопку "Открыть снова" не показываем для приватных кейсов — грант гасится после открытия */}
                  {!caseData?.is_private && (
                    <button 
                      onClick={handleOpenAgain}
                      className="flex items-center gap-1.5 text-accent-primary hover:text-accent-secondary transition-colors"
                    >
                      <RotateCcw size={16} />
                      <span className="whitespace-nowrap">{openAgainLabel}</span>
                    </button>
                  )}
                  
                  {!caseData?.is_private && <span className="text-text-secondary/50"> </span>}
                  
                  <button 
                    onClick={handleBackToInventory}
                    className="flex items-center gap-1.5 text-text-secondary hover:text-white transition-colors"
                  >
                    <Package size={16} />
                    <span className="whitespace-nowrap">{t('caseOpening.backToInventory')}</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default CaseOpening
